import {
  LoadingPercentageRanges,
  MediaStreamLabel,
  SignalingStatus,
  type MediaStreamId,
} from "../constants/webrtc";
import type {
  SignalingConfig,
  SignalingCallbacks,
  SignalingMessage,
} from "../types";
import { getWindowResolution } from "../utils/system";

export class SignalingService {
  private websocket: WebSocket | null = null;
  private config: SignalingConfig;
  private callbacks: SignalingCallbacks;
  // private reconnectAttempts = 0;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private peerId: MediaStreamId;
  public status: SignalingStatus = SignalingStatus.DISCONNECTED;

  constructor(
    config: SignalingConfig,
    mediaStreamId: MediaStreamId,
    callbacks: SignalingCallbacks = {},
  ) {
    this.config = {
      reconnectAttempts: 20,
      reconnectDelay: 1000,
      ...config,
    };
    this.peerId = mediaStreamId;
    this.callbacks = callbacks;
  }

  private onClose(event: CloseEvent) {
    console.log(
      `[${
        MediaStreamLabel[this.peerId]
      }] - Signaling websocket closed, status: ${this.status}, code: ${
        event.code
      }, reason: ${event.reason}`,
    );

    if (this.status !== SignalingStatus.CONNECTING) {
      this.status = SignalingStatus.DISCONNECTED;

      this.callbacks.onChangeSignalingStatus?.(this.status);
      this.callbacks.onClose?.(event);
      this.websocket?.close();
    }
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        this.status = SignalingStatus.CONNECTED;
        this.callbacks.onChangeSignalingStatus?.(this.status);
        resolve();
        return;
      }

      if (this.status === SignalingStatus.CONNECTING) {
        reject(
          new Error(`[${MediaStreamLabel[this.peerId]}] - Already connecting`),
        );
        return;
      }

      this.status = SignalingStatus.CONNECTING;
      this.callbacks.onChangeSignalingStatus?.(this.status);
      this.callbacks.onLoadingPercentage?.(
        LoadingPercentageRanges.CONNECTING_TO_SERVER,
      );

      try {
        this.websocket = new WebSocket(this.config.url, this.config.protocols);

        this.websocket.onopen = () => {
          this.status = SignalingStatus.CONNECTED;
          this.callbacks.onChangeSignalingStatus?.(this.status);

          // this.reconnectAttempts = 0;
          this.callbacks.onOpen?.();
          this.callbacks.onLoadingPercentage?.(
            LoadingPercentageRanges.REGISTERING_WITH_SERVER,
          );

          // Send local device resolution and scaling with HELLO message.
          const [width, height] = getWindowResolution();
          const meta = {
            res: `${width}x${height}`,
            scale: window.devicePixelRatio,
          };

          this.websocket?.send(
            `HELLO ${this.peerId} ${btoa(JSON.stringify(meta))}`,
          );

          resolve();
        };

        this.websocket.onclose = this.onClose.bind(this);

        this.websocket.onerror = (event) => {
          console.error(
            `[${MediaStreamLabel[this.peerId]}] - WebSocket error event:`,
            event,
            `ReadyState: ${this.websocket?.readyState}`,
          );
          this.status = SignalingStatus.DISCONNECTED;
          this.callbacks.onChangeSignalingStatus?.(this.status);
          reject(
            new Error(
              `[${
                MediaStreamLabel[this.peerId]
              }] - WebSocket connection failed`,
            ),
          );
        };

        this.websocket.onmessage = (event) => {
          try {
            if (event.data === "HELLO") {
              console.log(
                `[${MediaStreamLabel[this.peerId]}] - Registered with server`,
              );
              this.callbacks.onLoadingPercentage?.(
                LoadingPercentageRanges.REGISTERED_WITH_SERVER,
              );
              return;
            }
            const message: SignalingMessage = JSON.parse(event.data);
            this.onMessage(message);
          } catch (error) {
            console.error("Failed to parse signaling message:", error);
          }
        };
      } catch (error) {
        this.status = SignalingStatus.DISCONNECTED;
        this.callbacks.onChangeSignalingStatus?.(this.status);
        console.log(
          `[${MediaStreamLabel[this.peerId]}] - Failed to connect to websocket`,
          this.config.url,
          this.config.protocols,
        );
        reject(error);
      }
    });
  }

  disconnect(code?: number): void {
    console.log(`[${MediaStreamLabel[this.peerId]}] - Disconnect websocket`);
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.websocket) {
      this.websocket.onclose = null;
      this.websocket.close(code);
      this.websocket = null;
    }
    this.status = SignalingStatus.DISCONNECTED;
    this.callbacks.onChangeSignalingStatus?.(this.status);
  }

  send(message: SignalingMessage): void {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      this.websocket.send(JSON.stringify(message));
    } else {
      throw new Error(
        `[${MediaStreamLabel[this.peerId]}] - WebSocket is not connected`,
      );
    }
  }

  sendSDP(sdp: RTCSessionDescription): void {
    console.log(
      `[${MediaStreamLabel[this.peerId]}] [sendSDP]`,
      this.websocket?.readyState,
      sdp,
    );
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      this.websocket.send(JSON.stringify({ sdp }));
    } else {
      throw new Error(
        `[${MediaStreamLabel[this.peerId]}] - WebSocket is not connected`,
      );
    }
  }

  sendIceCandidate(candidate: RTCIceCandidate): void {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      this.websocket.send(JSON.stringify({ ice: candidate }));
    } else {
      throw new Error(
        `[${MediaStreamLabel[this.peerId]}] - WebSocket is not connected`,
      );
    }
  }

  isConnected(): boolean {
    return this.websocket?.readyState === WebSocket.OPEN;
  }

  private onMessage(message: SignalingMessage): void {
    this.callbacks.onMessage?.(message);
  }
}
